/**
 * SKUs API Client
 * Fetches SKU and variant data for product selection
 */

import { getHttpClient, normalizeEntity, normalizeRelationArray } from '@ufc/core'

// ============================================
// Constants
// ============================================

const SKU_ENDPOINTS = {
  LIST: '/api/skus',
  PORTAL_PRODUCTS: '/api/catalog/products',
} as const

const MAX_PAGE_SIZE = 1000

// ============================================
// Types
// ============================================

interface SkuCategory {
  id?: string | number
  documentId?: string
  code?: string
  name?: string
}

interface SkuVariant {
  id?: string | number
  documentId?: string
  name?: string
  skuCode?: string
  variantCode?: string // Strapi field name (alias for skuCode)
}

interface PricedVariant extends SkuVariant {
  unitPrice?: number | null
  priceType?: string | null
  minQuantity?: number | null
  maxQuantity?: number | null
  minOrderQuantity?: number | null
  maxOrderQuantity?: number | null
  isAvailable?: boolean | null
}

interface PricedSku extends Sku {
  variants?: PricedVariant[]
}

interface Sku {
  id?: string | number
  documentId?: string
  name?: string
  code?: string
  categories?: unknown
  variants?: unknown
}

// ============================================
// Helper Functions
// ============================================

function buildPopulateParams(): URLSearchParams {
  const params = new URLSearchParams()
  params.set('populate[categories]', 'true')
  params.set('populate[variants]', 'true')
  // Populate localizations (Strapi i18n - get both en and ar in single request)
  params.set('populate[localizations][fields][0]', 'locale')
  params.set('populate[localizations][fields][1]', 'name')
  params.set('populate[localizations][fields][2]', 'description')
  params.set('pagination[pageSize]', String(MAX_PAGE_SIZE))
  return params
}

// ============================================
// API Client
// ============================================

export const skusApi = {
  /**
   * Get all SKUs with categories and variants populated
   */
  async getSkus(): Promise<Sku[]> {
    const http = getHttpClient()
    const params = buildPopulateParams()

    const response = await http.get<unknown>(`${SKU_ENDPOINTS.LIST}?${params}`)
    const responseData = response.data as Record<string, unknown>
    const raw = Array.isArray(responseData?.data) ? responseData.data : []

    return raw.map(item => normalizeEntity<Sku>(item)).filter((sku): sku is Sku => sku !== null)
  },

  /**
   * Extract categories from a SKU entity
   */
  extractCategories(sku: unknown): SkuCategory[] {
    if (!sku || typeof sku !== 'object') return []
    const skuRecord = sku as Record<string, unknown>
    const categories = normalizeRelationArray<SkuCategory>(skuRecord.categories)
    return categories
  },

  /**
   * Extract variants from a SKU entity
   */
  extractVariants(sku: unknown): SkuVariant[] {
    if (!sku || typeof sku !== 'object') return []
    const skuRecord = sku as Record<string, unknown>
    const variants = normalizeRelationArray<SkuVariant>(skuRecord.variants)
    return variants
  },

  /**
   * Extract priced variants from a SKU entity (includes unitPrice)
   */
  extractPricedVariants(sku: unknown): PricedVariant[] {
    if (!sku || typeof sku !== 'object') return []
    const skuRecord = sku as Record<string, unknown>
    const variants = normalizeRelationArray<PricedVariant>(skuRecord.variants)
    return variants
  },

  /**
   * Get all products with pricing from ecommerce endpoint
   * Returns SKUs with variants that include unitPrice
   *
   * Availability filtering priority:
   * 1. factoryId - filter by specific factory (recommended)
   * 2. cityId - filter by factories serving a city
   * 3. regionId - legacy, filter by region (deprecated)
   *
   * When locale=ar: fetches EN (for category/structure) and AR (for names)
   * in parallel, then merges — because the catalog API returns empty categories
   * for the Arabic locale.
   */
  async getProductsWithPricing(options?: {
    factoryId?: string
    cityId?: string
    regionId?: string
    locale?: string
  }): Promise<PricedSku[]> {
    const http = getHttpClient()

    // Build headers based on available options (priority: factoryId > cityId > regionId)
    const headers: Record<string, string> = {}
    if (options?.factoryId) {
      headers['x-factory-id'] = options.factoryId
    } else if (options?.cityId) {
      headers['x-city-id'] = options.cityId
    } else if (options?.regionId) {
      headers['x-region-id'] = options.regionId
    }

    const requestHeaders = Object.keys(headers).length > 0 ? headers : undefined

    const fetchForLocale = async (locale: string): Promise<PricedSku[]> => {
      const params = new URLSearchParams()
      params.set('locale', locale)
      const endpoint = `${SKU_ENDPOINTS.PORTAL_PRODUCTS}?${params.toString()}`
      const response = await http.get<unknown>(endpoint, { headers: requestHeaders })
      const responseData = response.data as Record<string, unknown>
      const raw = Array.isArray(responseData?.data) ? responseData.data : []
      return raw.map(item => normalizeEntity<PricedSku>(item)).filter((sku): sku is PricedSku => sku !== null)
    }

    try {
      const locale = options?.locale

      // When Arabic is requested: fetch EN for structure (categories are populated
      // only in EN locale) and AR for translated names, then merge.
      if (locale === 'ar') {
        const [enSkus, arSkus] = await Promise.all([
          fetchForLocale('en'),
          fetchForLocale('ar').catch(() => [] as PricedSku[]),
        ])

        // Build AR name lookup: documentId → { name, variantNames }
        type ArEntry = { name?: string; variantNames: Map<string, string> }
        const arMap = new Map<string, ArEntry>()
        for (const arSku of arSkus) {
          if (!arSku.documentId) continue
          const variantNames = new Map<string, string>()
          const arVariants = Array.isArray(arSku.variants) ? (arSku.variants as PricedVariant[]) : []
          for (const v of arVariants) {
            if (v.documentId && v.name) variantNames.set(v.documentId, v.name)
          }
          arMap.set(arSku.documentId, { name: arSku.name, variantNames })
        }

        // Merge: EN structure + AR names
        return enSkus.map((sku) => {
          const ar = sku.documentId ? arMap.get(sku.documentId) : undefined
          if (!ar) return sku

          const mergedVariants = Array.isArray(sku.variants)
            ? (sku.variants as PricedVariant[]).map((v) => {
                const arName = v.documentId ? ar.variantNames.get(v.documentId) : undefined
                return arName ? { ...v, name: arName } : v
              })
            : sku.variants

          return { ...sku, name: ar.name ?? sku.name, variants: mergedVariants }
        })
      }

      // Non-Arabic locale: single fetch
      if (locale) {
        return fetchForLocale(locale)
      }

      const response = await http.get<unknown>(SKU_ENDPOINTS.PORTAL_PRODUCTS, { headers: requestHeaders })
      const responseData = response.data as Record<string, unknown>
      const raw = Array.isArray(responseData?.data) ? responseData.data : []
      return raw.map(item => normalizeEntity<PricedSku>(item)).filter((sku): sku is PricedSku => sku !== null)
    } catch (error) {
      console.error('Portal catalog products endpoint failed:', error)
      throw error
    }
  },
}
